import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { isSameMonth } from "date-fns";
import { useMemo, useState } from "react";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { FormattedDuration } from "@/components/FormattedDuration/FormattedDuration";
import { Badge } from "@/design-system/Badge";
import { createDashboardColumnHelper, TABLE_COLUMN_SIZES } from "@/design-system/Table/helpers";
import { useBool } from "@/hooks/useBool";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { useAnalyticsScope } from "@/modules/analytics/AnalyticsScopeContext";
import { ANALYTICS_TABLE_PAGE_SIZE } from "@/modules/analytics/constants";
import type {
  BookingAnalyticsAssetRow,
  BookingAnalyticsBookingRow,
  BookingAnalyticsDateRange,
  BookingAnalyticsDurationBucket,
  BookingAnalyticsTimeOfDay,
  BookingAnalyticsWeekday,
} from "@/queries/analytics/booking-reporting-types";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { useTranslation } from "@/translations";
import type { NonEmptyArray } from "@/types/utility-types";

import { hasMultipleBookingOrigins } from "./booking-origins";
import { BookingAnalyticsContent, type BookingAnalyticsTableTab } from "./BookingAnalyticsContent";
import { BookingAnalyticsFilterDrawer, type BookingAnalyticsFilterValues } from "./BookingAnalyticsFilterDrawer";
import { isDurationInBucket } from "./chart-filters";

export interface BookingAnalyticsDateRangeOption {
  value: BookingAnalyticsDateRange;
  name: string;
}

export type BookingAnalyticsChartFilter =
  | {
      type: "month";
      value: string;
      label: string;
    }
  | {
      type: "weekday";
      value: BookingAnalyticsWeekday;
      label: string;
    }
  | {
      type: "timeOfDay";
      value: BookingAnalyticsTimeOfDay;
      label: string;
    }
  | {
      type: "duration";
      value: BookingAnalyticsDurationBucket;
      label: string;
    }
  | undefined;

export function BookingAnalyticsPage({ mode = "focused" }: { mode?: "focused" | "all" }): React.ReactNode {
  const { i18n, t } = useTranslation();
  const analyticsQueries = useAnalyticsQueries();
  const analyticsScope = useAnalyticsScope();
  const [selectedDateRange, setSelectedDateRange] = useState<BookingAnalyticsDateRange>("last6Months");
  const [selectedTableTab, setSelectedTableTab] = useState<BookingAnalyticsTableTab>("bookings");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [chartFilter, setChartFilter] = useState<BookingAnalyticsChartFilter>();
  const [filter, setFilter] = useState<BookingAnalyticsFilterValues>({
    assetId: "all",
    origin: "all",
    status: "all",
  });
  const [isFilterOpened, filterOpenHandlers] = useBool();

  const dateRangeOptions = useMemo<NonEmptyArray<BookingAnalyticsDateRangeOption>>(
    () => [
      { value: "last6Months", name: t("common.date-range.last-months", { count: 6 }) },
      { value: "lastYear", name: t("common.date-range.last-year") },
    ],
    [t],
  );
  const selectedDateRangeOption = dateRangeOptions.find((option) => option.value === selectedDateRange)!;
  const query = {
    dateRange: selectedDateRange,
    assetId: filter.assetId === "all" ? undefined : filter.assetId,
    origin: filter.origin === "all" ? undefined : filter.origin,
    status: filter.status === "all" ? undefined : filter.status,
  };
  const projectResult = useQuery({
    ...analyticsQueries.bookingReporting(query),
    placeholderData: keepPreviousData,
    enabled: analyticsScope.type === "project",
  });
  const portfolioResult = useQuery({
    ...analyticsQueries.portfolioBookingReporting({
      projectIds: analyticsScope.type === "portfolio" ? analyticsScope.projectIds : [],
      dateRange: query.dateRange,
      origin: query.origin,
      status: query.status,
    }),
    placeholderData: keepPreviousData,
    enabled: analyticsScope.type === "portfolio" && !analyticsScope.isLoading,
  });
  const { data, isLoading, isFetching, error } = analyticsScope.type === "portfolio" ? portfolioResult : projectResult;
  const showOrigin = hasMultipleBookingOrigins(data?.originCounts);

  const filteredBookings = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase();

    return (data?.bookings ?? []).filter((booking) => {
      const matchesSearch =
        !normalizedSearch ||
        booking.assetName.toLocaleLowerCase().includes(normalizedSearch) ||
        booking.authorName.toLocaleLowerCase().includes(normalizedSearch);

      if (!matchesSearch || !chartFilter) {
        return matchesSearch;
      }

      if (chartFilter.type === "month") {
        return isSameMonth(new Date(booking.startsAt), new Date(chartFilter.value));
      }
      if (chartFilter.type === "weekday") {
        return booking.weekday === chartFilter.value;
      }
      if (chartFilter.type === "timeOfDay") {
        return booking.timeOfDay === chartFilter.value;
      }

      return isDurationInBucket(booking.durationHours, chartFilter.value);
    });
  }, [chartFilter, data?.bookings, search]);
  const filteredAssets = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase();

    if (!normalizedSearch) {
      return data?.assets ?? [];
    }

    return (data?.assets ?? []).filter((asset) => asset.name.toLocaleLowerCase().includes(normalizedSearch));
  }, [data?.assets, search]);
  const resultCount = selectedTableTab === "bookings" ? filteredBookings.length : filteredAssets.length;
  const pageCount = Math.max(1, Math.ceil(resultCount / ANALYTICS_TABLE_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const paginatedBookings = filteredBookings.slice(
    currentPage * ANALYTICS_TABLE_PAGE_SIZE,
    (currentPage + 1) * ANALYTICS_TABLE_PAGE_SIZE,
  );
  const paginatedAssets = filteredAssets.slice(
    currentPage * ANALYTICS_TABLE_PAGE_SIZE,
    (currentPage + 1) * ANALYTICS_TABLE_PAGE_SIZE,
  );
  const bookingColumns = useMemo(() => {
    const helper = createDashboardColumnHelper<BookingAnalyticsBookingRow>();

    return [
      helper.accessor("assetName", {
        header: t("page.analytics-bookings.table.asset"),
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("origin", {
        header: t("page.analytics-bookings.table.origin"),
        cell: (cell) => (
          <Badge variant="special">
            {cell.row.original.projectConnectionName ?? t("page.analytics-bookings.origin.project-only")}
          </Badge>
        ),
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("startsAt", {
        header: t("page.analytics-bookings.table.date"),
        cell: (cell) => <span>{formatDate(i18n, "datetimeShort", cell.getValue())}</span>,
        minSize: TABLE_COLUMN_SIZES.DATE_TIME.MIN,
        size: TABLE_COLUMN_SIZES.DATE_TIME.DEFAULT,
      }),
      helper.accessor("authorName", {
        header: t("page.analytics-bookings.table.booker"),
        minSize: TABLE_COLUMN_SIZES.USER.MIN,
        size: TABLE_COLUMN_SIZES.USER.DEFAULT,
      }),
      helper.accessor("durationHours", {
        header: t("page.analytics-bookings.table.duration"),
        cell: (cell) => <FormattedDuration duration={{ hours: cell.getValue() }} formatTokens={["hours", "minutes"]} />,
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("status", {
        header: t("page.analytics-bookings.table.status"),
        cell: (cell) => (
          <Badge variant={getBookingStatusVariant(cell.getValue())}>
            {t(`page.analytics-bookings.filters.status.${cell.getValue()}` as const)}
          </Badge>
        ),
        minSize: TABLE_COLUMN_SIZES.STATUS.MIN,
        size: TABLE_COLUMN_SIZES.STATUS.DEFAULT,
      }),
    ];
  }, [i18n, t]);
  const bookingTable = useResizableTable(TABLE_KEYS.ANALYTICS_BOOKINGS, {
    columns: bookingColumns,
    data: paginatedBookings,
    state: {
      columnVisibility: {
        origin: showOrigin,
      },
    },
    initialState: {
      sorting: [{ id: "startsAt", desc: true }],
    },
  });

  const assetColumns = useMemo(() => {
    const helper = createDashboardColumnHelper<BookingAnalyticsAssetRow>();

    return [
      helper.accessor("name", {
        header: t("page.analytics-bookings.assets-table.asset"),
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("origin", {
        header: t("page.analytics-bookings.table.origin"),
        cell: (cell) => (
          <Badge variant="special">
            {cell.row.original.projectConnectionName ?? t("page.analytics-bookings.origin.project-only")}
          </Badge>
        ),
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("bookings", {
        header: t("page.analytics-bookings.assets-table.bookings"),
        minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
        size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
      }),
      helper.accessor("uniqueBookers", {
        header: t("page.analytics-bookings.assets-table.bookers"),
        minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
        size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
      }),
      helper.accessor("bookedHours", {
        header: t("page.analytics-bookings.assets-table.hours"),
        cell: (cell) => <FormattedDuration duration={{ hours: cell.getValue() }} formatTokens={["hours", "minutes"]} />,
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("utilizationPercentage", {
        header: t("page.analytics-bookings.assets-table.utilization"),
        cell: (cell) => <span>{cell.getValue()}%</span>,
        minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
        size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
      }),
      helper.accessor("cancellationPercentage", {
        header: t("page.analytics-bookings.assets-table.cancellations"),
        cell: (cell) => <span>{cell.getValue()}%</span>,
        minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
        size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
      }),
      helper.accessor("enabled", {
        header: t("page.analytics-bookings.assets-table.status"),
        cell: (cell) => (
          <Badge variant={cell.getValue() ? "success" : "default"}>
            {cell.getValue()
              ? t("page.analytics-bookings.assets-table.active")
              : t("page.analytics-bookings.assets-table.inactive")}
          </Badge>
        ),
        minSize: TABLE_COLUMN_SIZES.STATUS.MIN,
        size: TABLE_COLUMN_SIZES.STATUS.DEFAULT,
      }),
    ];
  }, [t]);
  const assetTable = useResizableTable(TABLE_KEYS.ANALYTICS_BOOKABLE_ASSETS, {
    columns: assetColumns,
    data: paginatedAssets,
    state: {
      columnVisibility: {
        origin: showOrigin,
      },
    },
    initialState: {
      sorting: [{ id: "bookings", desc: true }],
    },
  });

  const activeFilterCount =
    Number(filter.assetId !== "all") + Number(filter.origin !== "all") + Number(filter.status !== "all");
  const handleDateRangeChange = (option: BookingAnalyticsDateRangeOption) => {
    setSelectedDateRange(option.value);
    setChartFilter(undefined);
    setPage(0);
  };
  const handleTableTabChange = (tab: BookingAnalyticsTableTab) => {
    setSelectedTableTab(tab);
    setSearch("");
    setPage(0);
  };
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(0);
  };
  const handleFilterUpdate = (value: BookingAnalyticsFilterValues) => {
    setFilter(value);
    setChartFilter(undefined);
    setPage(0);
  };
  const handleAssetFilter = (assetId: string) => {
    setFilter((current) => ({ ...current, assetId }));
    setSelectedTableTab("bookings");
    setChartFilter(undefined);
    setPage(0);
  };
  const handleChartFilter = (value: NonNullable<BookingAnalyticsChartFilter>) => {
    setChartFilter(value);
    setSelectedTableTab("bookings");
    setPage(0);
  };

  if (error) {
    return <ErrorPage error={error} />;
  }

  return (
    <>
      <BookingAnalyticsContent
        mode={mode}
        data={data}
        isLoading={isLoading}
        isFetching={isFetching}
        selectedDateRange={selectedDateRangeOption}
        dateRangeOptions={dateRangeOptions}
        onDateRangeChange={handleDateRangeChange}
        selectedTableTab={selectedTableTab}
        onTableTabChange={handleTableTabChange}
        search={search}
        onSearchChange={handleSearchChange}
        activeFilterCount={activeFilterCount}
        onOpenFilters={filterOpenHandlers.setTrue}
        bookingTable={bookingTable}
        assetTable={assetTable}
        chartFilter={chartFilter}
        onChartFilter={handleChartFilter}
        onAssetFilter={handleAssetFilter}
        onClearChartFilter={() => setChartFilter(undefined)}
        page={currentPage}
        pageCount={pageCount}
        bookingResultCount={filteredBookings.length}
        assetResultCount={filteredAssets.length}
        onPageChange={setPage}
      />
      <BookingAnalyticsFilterDrawer
        isOpened={isFilterOpened}
        onOpenChange={filterOpenHandlers.set}
        tableTab={selectedTableTab}
        filter={filter}
        assets={data?.filterOptions.assets ?? []}
        showOriginFilter={showOrigin}
        onUpdate={handleFilterUpdate}
      />
    </>
  );
}

function getBookingStatusVariant(status: BookingAnalyticsBookingRow["status"]): "success" | "warning" | "default" {
  if (status === "completed") {
    return "success";
  }
  if (status === "upcoming") {
    return "warning";
  }

  return "default";
}
